import React from "react";
import PackageCard from "./PackageCard";
import { AddOn, Pricing } from "../../types/Response/CustomerResponse";

// Extended interface to safely support both mapped UI objects and direct API response objects
export type SummaryAddOn = AddOn & {
    id?: number | string;
    title?: string;
    image?: string;
};

interface PackageItem {
    packageId: number;
    packageTitle: string;
    packageSubtitle?: string;
    description: string;
    pricing: Pricing;
    includedBookings?:number
}

interface BookingCartSummaryProps {
    packageItem: PackageItem;
    addOns?: AddOn[];
    totalPrice: number;
}

const BookingCartSummary: React.FC<BookingCartSummaryProps> = ({
    packageItem,
    addOns = [],
    totalPrice,
}) => {
    return (
        <div className="bg-white rounded-[2rem] border border-[#ECE7DF] overflow-hidden">
            {/* Main Package Details Card */}
            {packageItem && (
                <PackageCard
                    packageId={packageItem.packageId}
                    packageTitle={packageItem.packageTitle}
                    packageSubtitle={packageItem.packageSubtitle}
                    description={packageItem.description}
                    includedBookings={packageItem.includedBookings}
                    pricing={packageItem.pricing}
                    hideButton
                    disableHover
                    className="rounded-b-[0rem]"
                />
            )}

            {/* Added Add-ons List */}
            {addOns.length > 0 && (
                <div className="p-5 space-y-4">
                    <h4 className="font-semibold text-logoHeading">
                        Added Services
                    </h4>

                    {addOns.map((item, index) => {
                        // Extract properties safely regardless of property naming
                        const key = item.addOnId;
                        const title = item.name ;
                        const imageUrl = item.imageUrl ;

                        return (
                            <div
                                key={key}
                                className="flex gap-3 items-center"
                            >
                                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt={title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xs text-gray-400">
                                            No Image
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-logoHeading text-sm truncate">
                                        {title}
                                    </p>

                                    {item.description && (
                                        <p className="text-xs text-gray-500 line-clamp-2">
                                            {item.description}
                                        </p>
                                    )}
                                </div>

                                <span className="font-bold text-mainBrandColor text-sm whitespace-nowrap">
                                    AED {item.price}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Summary Footer */}
            <div className="border-t px-5 py-3 bg-[#FAF8F5]">
                <div className="flex justify-between items-center">
                    <span className="font-semibold text-logoHeading">
                        Total
                    </span>

                    <span className="text-xl font-bold text-logoHeading">
                        AED {totalPrice}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default BookingCartSummary;